import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type WorkspaceSessionsResult } from '@whinerp/client';
import { formatRelativeDate } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Who is signed in, and signing people out (G-281). Grouped by person,
 * because the question is "is Sara still signed in anywhere", not "what is
 * session 4f1c".
 */
export function SessionsPanel(): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const enabled = can('setup.users.manage');
  const [reason, setReason] = useState('');
  const sessions = useQuery<WorkspaceSessionsResult, ApiError>({ queryKey: ['workspace-sessions'], queryFn: () => api.workspaceSessions({}), enabled });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workspace-sessions'] });
  const force = useMutation<unknown, ApiError, string>({
    mutationFn: (userId) => api.forceSignOut({ body: { userId, reason } }),
    onSuccess: async () => {
      setReason('');
      await refresh();
    },
  });
  const others = useMutation<{ ended: number }, ApiError>({ mutationFn: () => api.signOutOtherDevices({}), onSuccess: () => refresh() });
  if (!enabled || !sessions.data) return null;
  const people = new Map<string, { name: string; email: string; rows: WorkspaceSessionsResult['sessions'] }>();
  for (const row of sessions.data.sessions) {
    const entry = people.get(row.userId) ?? { name: row.userName, email: row.email, rows: [] };
    entry.rows.push(row);
    people.set(row.userId, entry);
  }
  const mine = sessions.data.sessions.find((row) => row.current)?.userId ?? null;
  const failure = force.error ?? others.error;
  return (
    <Panel data-region="sessions">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-col gap-1">
            <SectionLabel>{t('sessions.title')}</SectionLabel>
            <p className="text-meta text-ink-meta">{t('sessions.body')}</p>
          </div>
          <Button intent="secondary" size="small" className="ms-auto" data-sign-out-others onClick={() => others.mutate()}>
            {t('sessions.signOutOthers')}
          </Button>
        </div>
        {others.data ? <p className="text-meta text-ink-meta" data-signed-out-others>{t('sessions.othersEnded', { values: { count: String(others.data.ended) } })}</p> : null}
        {failure ? <p data-sessions-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">{failure.message}</p> : null}
        <label className="flex flex-col gap-1 text-dense font-medium text-ink">
          {t('sessions.reason')}
          <input data-sessions-reason className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink" value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        <ul className="flex flex-col">
          {[...people.entries()].map(([userId, person]) => (
            <li key={userId} data-session-person={userId} className="flex flex-col gap-1 border-t border-t-rule py-2 first:border-t-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-body text-ink">{person.name}</span>
                <span className="text-meta text-ink-meta">{person.email}</span>
                <span className="text-meta text-ink-meta">{t('sessions.count', { values: { count: String(person.rows.length) } })}</span>
                {userId === mine ? null : (
                  <Button intent="ghost" size="small" className="ms-auto" data-force-sign-out={userId} disabled={reason.trim() === '' || force.isPending} title={reason.trim() === '' ? t('sessions.reasonFirst') : undefined} onClick={() => force.mutate(userId)}>
                    {t('sessions.forceSignOut')}
                  </Button>
                )}
              </div>
              <ul className="flex flex-col text-meta text-ink-meta">
                {person.rows.map((row) => (
                  <li key={row.sessionId} data-session={row.sessionId}>
                    {row.deviceLabel ?? row.userAgent ?? t('sessions.unknownDevice')}
                    {row.ip ? ` · ${row.ip}` : ''}
                    {` · ${t('sessions.lastSeen', { values: { when: formatRelativeDate(row.lastSeenAt, format) } })}`}
                    {row.current ? ` · ${t('sessions.thisOne')}` : ''}
                    {row.impersonated ? ` · ${t('sessions.support')}` : ''}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
